import React, { useState } from 'react';
import { Radio } from 'antd';
import FarmIcon from '../../assets/icons/organisation.svg';
import { useTranslation } from 'react-i18next';
import Spinner from '../Spinner/Spinner';
import {
  ModalContainer,
  SuccessModal,
  SuccessHeader,
  SuccessIcon,
  SuccessTitle,
  SuccessMessage,
  CloseButton,
  Overlay,
  ContentContainer,
  TitleContainer,
  Icon,
  Title,
  Body,
  ButtonContainer,
  Button,
  ErrorMessage,
  StyledInput,
  ReasonModalContainer,
  ReasonTitle,
  StyledRadioGroup,
  ReasonButtonContainer,
  ReasonButton,
  LeftButtonGroup,
  RightButtonGroup,
  DontShareButton,
  ChangeEmailButton,
  ShareButton,
  ChangeEmailTitle,
  BodyLeft,
  InputContainer,
  TickIcon,
  Divider,
  MainButtonContainer,
  ChangeEmailButtonContainer,
  AddEmailButton,
} from './ShareRecommendationModal.styles';
import ProxyLayer from 'base/api/ProxyLayer';
import {
  trackShareRecommendationInput,
  trackReasonForNotSharing,
  trackChangeEmail,
} from 'utils/helpers/amplitude';
import { getCropName } from 'pages/RecommendationV2/Sections/HeroBanner/heroBannerUtils';

interface ShareRecommendationModalProps {
  email: string;
  onClose: () => void;
  growerName: string;
  tsrName: string;
  recommendation_url: string;
  solutionId: string;
  recommData: any;
}

const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

const validateEmail = (email: string) => {
  return emailRegex.test(email);
};

const ShareRecommendationModal: React.FC<ShareRecommendationModalProps> = ({
  email,
  growerName,
  tsrName,
  recommendation_url,
  solutionId,
  recommData,
  onClose,
}) => {
  // console.log('recommendation_data', recommData);

  const [t] = useTranslation();
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [showChangeEmailPopup, setShowChangeEmailPopup] = useState(false);
  const [newEmail, setNewEmail] = useState(email);
  const [emailError, setEmailError] = useState<string>('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleDontShareClick = () => {
    setShowReasonModal(true);
  };

  const handleBackClick = () => {
    setShowReasonModal(false);
  };

  const handleReasonChange = (e: any) => {
    const newReason = e.target.value;
    setSelectedReason(newReason);
  };
  const handleConfirmClick = () => {
    if (selectedReason) {
      // Immediately hide the modal
      setShowReasonModal(false);
      onClose();

      // Prepare the data for updating
      const updatedRecommData = JSON.parse(JSON.stringify(recommData));
      const agInputs =
        updatedRecommData?.recommendations?.[0]?.multi_field?.config?.agronomicInputs;

      if (agInputs) {
        if (!agInputs.extended_attributes) {
          agInputs.extended_attributes = {};
        }
        agInputs.extended_attributes.reason = selectedReason;
        agInputs.extended_attributes.isshared = true; // Mark as shared when a reason is selected

        if (selectedReason === 'Remind me to share in 3 days') {
          agInputs.extended_attributes.timer = Date.now(); // current timestamp in ms
        } else {
          delete agInputs.extended_attributes.timer;
        }
      }

      // Process the API call in the background (no await)
      ProxyLayer.updateRecommendation({
        data: updatedRecommData,
      })
        .then(() => {
          trackReasonForNotSharing(selectedReason);
        })
        .catch((error) => {
          console.error('Failed to update reason:', error);
        });
    }
  };
  const handleShare = async () => {
    setLoading(true);
    const applicationUrl = `${window.location.origin}`;
    const baseReportUrl = applicationUrl + '/recommendation/' + recommendation_url;
    const reportUrl = `${baseReportUrl}?from=${encodeURIComponent(tsrName)}`;
    const emailToSend = showChangeEmailPopup ? newEmail : email;
    const langFromRecommendationPage = sessionStorage.getItem('langFromRecommendationPage');

    const rec = (recommData?.recommendations || [])[0] || {};
    const crop = getCropName(rec, recommData, t);
    const recommendation = (rec.products || [])
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((p: any) => p.commercial_name ?? p.product_name)
      .filter(Boolean)
      .join(', ');
    const property = rec?.multi_field?.geometry?.properties?.[0];
    const propertyLocation = [property?.city, property?.state].filter(Boolean).join(', ');
    const location = propertyLocation || recommData?.organization_address || '';

    // Prepare the payload for sharing
    const payload = {
      email: emailToSend,
      placeholders: {
        grower_name: growerName,
        tsr_name: tsrName,
        report_link: reportUrl,
        solution_created_on: recommData.created_on,
        location,
        crop,
        recommendation,
      },
      sender: 'Syngenta Cropwise',
      language: langFromRecommendationPage || 'en',
    };

    try {
      await ProxyLayer.shareRecommendation(payload);

      const updatedRecommData = JSON.parse(JSON.stringify(recommData));
      const agInputs =
        updatedRecommData?.recommendations?.[0]?.multi_field?.config?.agronomicInputs;

      if (agInputs) {
        if (!agInputs.extended_attributes) {
          agInputs.extended_attributes = {};
        }
        agInputs.extended_attributes.isshared = true;
      }

      await ProxyLayer.updateRecommendation({
        data: updatedRecommData,
      });

      setShowSuccessMessage(true);
      trackShareRecommendationInput(emailToSend);
      if (newEmail) {
        trackChangeEmail(newEmail, email);
      }
    } catch (error) {
      console.error('Error sharing recommendation:', error);
      onClose();
    } finally {
      setLoading(false); // Stop loader
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessMessage(false);
    onClose();
  };

  const handleChangeEmailClick = () => {
    setNewEmail(email);
    setShowChangeEmailPopup(true);
    setEmailError('');
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewEmail(value);

    if (!value) {
      setEmailError(t('Email is required'));
    } else if (!validateEmail(value)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handleEmailPopupClose = () => {
    setShowChangeEmailPopup(false);
    setEmailError('');
    setNewEmail(email);
  };

  const renderSuccessModal = () => (
    <SuccessModal>
      <SuccessHeader>
        <SuccessIcon>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13.3334 4L6.00008 11.3333L2.66675 8"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </SuccessIcon>
        <SuccessTitle>{t('Success!')}</SuccessTitle>
      </SuccessHeader>
      <SuccessMessage>
        {t('Recommendation shared with the grower')}{' '}
        <strong>{showChangeEmailPopup ? newEmail : email}</strong>.
      </SuccessMessage>
      <CloseButton onClick={handleSuccessClose}>{t('Close')}</CloseButton>
    </SuccessModal>
  );

  const renderInitialModal = () => (
    <ModalContainer>
      <ContentContainer>
        <TitleContainer>
          <Icon src={FarmIcon} alt="Farm Icon" />
          <Title>{t('Share Recommendation')}</Title>
        </TitleContainer>

        {email ? (
          <Body>
            {t('Recommendation is getting shared with')} <strong>{email}</strong>.{' '}
            {t('Please ensure the email is correct.')}
          </Body>
        ) : (
          <Body>{t('Please add the Grower’s email to share this recommendation')}</Body>
        )}
      </ContentContainer>
      <MainButtonContainer>
        <RightButtonGroup>
          {email ? (
            <>
              <ChangeEmailButton data-btn="change-email" onClick={handleChangeEmailClick}>
                {t('Change Email')}
              </ChangeEmailButton>
              <ShareButton
                data-btn="share"
                disabled={!email || !validateEmail(email)}
                onClick={handleShare}
              >
                {t('Share')}
              </ShareButton>
            </>
          ) : (
            <AddEmailButton data-btn="Add-email" onClick={handleChangeEmailClick}>
              {t('Add Email')}
            </AddEmailButton>
          )}
        </RightButtonGroup>
        <LeftButtonGroup>
          <DontShareButton data-btn="dont-share" onClick={handleDontShareClick}>
            {t('Dont share')}
          </DontShareButton>
        </LeftButtonGroup>
      </MainButtonContainer>
    </ModalContainer>
  );

  const renderReasonModal = () => (
    <ReasonModalContainer>
      <div>
        <ReasonTitle>{t('Please select a reason')}</ReasonTitle>
        <Divider />
        <StyledRadioGroup onChange={handleReasonChange} value={selectedReason}>
          <Radio value="Grower's Email Unknown">{t('Growers Email Unknown')}</Radio>
          <Radio value="Grower confirmed recommendation in-person">
            {t('Grower confirmed recommendation in-person')}
          </Radio>
          <Radio value="Recommendation created as a Test">
            {t('Recommendation created as a Test')}
          </Radio>
          <Radio value="Remind me to share in 3 days">{t('Remind me to share in 3 days')}</Radio>
        </StyledRadioGroup>
      </div>
      <Divider />
      <ReasonButtonContainer>
        <ReasonButton onClick={handleBackClick}>{t('Back')}</ReasonButton>
        <ReasonButton disabled={!selectedReason} onClick={handleConfirmClick}>
          {t('Confirm')}
        </ReasonButton>
      </ReasonButtonContainer>
    </ReasonModalContainer>
  );

  const renderChangeEmailPopup = () => (
    <ModalContainer>
      <ChangeEmailTitle>{email ? t('Change Email') : t('Add Email')}</ChangeEmailTitle>
      <BodyLeft>{t('Enter the new email address')}</BodyLeft>
      <InputContainer>
        <StyledInput
          type="email"
          value={newEmail}
          onChange={handleEmailChange}
          hasError={!!emailError}
          placeholder={t('Enter email address')}
        />
        {validateEmail(newEmail) && (
          <TickIcon>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            >
              <path
                d="M6.59631 11.9077L3.46881 8.78016L2.40381 9.83766L6.59631 14.0302L15.5963 5.03016L14.5388 3.97266L6.59631 11.9077Z"
                fill="#696F88"
              />
            </svg>
          </TickIcon>
        )}
        {!validateEmail(newEmail) && newEmail && (
          <TickIcon>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M9 1.5C4.86 1.5 1.5 4.86 1.5 9C1.5 13.14 4.86 16.5 9 16.5C13.14 16.5 16.5 13.14 16.5 9C16.5 4.86 13.14 1.5 9 1.5ZM8.25 12.75V11.25H9.75V12.75H8.25ZM8.25 5.25V9.75H9.75V5.25H8.25Z"
                fill="#EB4B4B"
              />
            </svg>
          </TickIcon>
        )}
      </InputContainer>
      {emailError && <ErrorMessage>{emailError}</ErrorMessage>}
      <ChangeEmailButtonContainer>
        <Button onClick={handleEmailPopupClose}>{t('Back')}</Button>
        <ShareButton
          disabled={!newEmail || !validateEmail(newEmail) || !!emailError}
          onClick={handleShare}
        >
          {t('Share')}
        </ShareButton>
      </ChangeEmailButtonContainer>
    </ModalContainer>
  );

  return (
    <>
      <Overlay />
      {loading ? (
        <Spinner />
      ) : showSuccessMessage ? (
        renderSuccessModal()
      ) : showReasonModal ? (
        renderReasonModal()
      ) : showChangeEmailPopup ? (
        renderChangeEmailPopup()
      ) : (
        renderInitialModal()
      )}
    </>
  );
};

export default ShareRecommendationModal;
